import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import { Job, Account } from "types/common/ServiceStoreTypes";

interface JobFormData {
  jobNumber: number | null;
  name: string;
  shortName: string;
  poNumber: string;
  site: string;
  address: string;
  city: string;
  stateLocation: string;
  zip: string;
  engineer: string;
  projectManager: string;
  fieldForeman: string;
  description: string;
  stage: string;
  floorCount: number | null;
  accountId: number | null;
  budget: number | null;
  actualCost: number | null;
  priority: string;
  progress: number;
}

interface ValidationErrors {
  jobNumber?: string;
  name?: string;
  stage?: string;
  priority?: string;
  accountId?: string;
  budget?: string;
}

export class ProjectFormStore {
  rootStore: RootStore;
  // Observables - Only form UI state
  formData: JobFormData = {
    jobNumber: null,
    name: "",
    shortName: "",
    poNumber: "",
    site: "",
    address: "",
    city: "",
    stateLocation: "",
    zip: "",
    engineer: "",
    projectManager: "",
    fieldForeman: "",
    description: "",
    stage: "",
    floorCount: null,
    accountId: null,
    budget: null,
    actualCost: null,
    priority: "Medium",
    progress: 0,
  };

  originalData: JobFormData | null = null;
  isEditing = false;
  jobId: number | null = null;
  error: string | null = null;
  validationErrors: ValidationErrors = {};
  isDirty = false;

  // Observable loading states
  loading = false;
  saving = false;
  loadingAccounts = false;
  availableAccounts: Account[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  // Service store access
  get jobStore(): ServiceStore {
    return this.rootStore.jobStore as ServiceStore;
  }

  get accountsStore(): ServiceStore {
    return this.rootStore.accountsStore as ServiceStore;
  }

  // Form validation
  get isValid(): boolean {
    this.validateForm();
    return Object.keys(this.validationErrors).length === 0;
  }

  get hasChanges(): boolean {
    if (!this.originalData) return true;
    return JSON.stringify(this.formData) !== JSON.stringify(this.originalData);
  }

  // Form management methods
  async initializeForm(jobId?: string) {
    this.loading = true;
    this.error = null;

    try {
      await this.loadAvailableAccounts();

      if (jobId) {
        await this.loadJob(parseInt(jobId));
      } else {
        this.resetForm();
      }
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to initialize form";
      console.error("Failed to initialize job form:", error);
    } finally {
      this.loading = false;
    }
  }

  private async loadJob(id: number) {
    try {
      const job = (await this.jobStore.get(id)) as unknown as Job;
      if (job) {
        this.isEditing = true;
        this.jobId = id;
        this.formData = {
          jobNumber: job.jobNumber || null,
          name: job.name || "",
          shortName: job.shortName || "",
          poNumber: job.poNumber || "",
          site: job.site || "",
          address: job.address || "",
          city: job.city || "",
          stateLocation: job.stateLocation || "",
          zip: job.zip || "",
          engineer: job.engineer || "",
          projectManager: job.projectManager || "",
          fieldForeman: job.fieldForeman || "",
          description: job.description || "",
          stage: job.stage || "",
          floorCount: job.floorCount || null,
          accountId: job.accountId || null,
          budget: job.budget || null,
          actualCost: job.actualCost || null,
          priority: job.priority || "Medium",
          progress: job.progress || 0,
        };
        this.originalData = { ...this.formData };
      }
    } catch (error) {
      throw new Error(`Failed to load job: ${error}`);
    }
  }

  private async loadAvailableAccounts() {
    this.loadingAccounts = true;
    try {
      const accounts = await this.accountsStore.find({
        query: { status: "Active" },
      });
      runInAction(() => {
        this.availableAccounts = (accounts?.data || []) as unknown as Account[];
      });
    } catch (error) {
      console.error("Failed to load accounts:", error);
    } finally {
      runInAction(() => {
        this.loadingAccounts = false;
      });
    }
  }

  // Form field updates
  updateField<K extends keyof JobFormData>(
    field: K,
    value: JobFormData[K]
  ) {
    this.formData[field] = value;
    this.isDirty = true;
    this.clearFieldError(field as string);
  }

  private clearFieldError(field: string) {
    if (this.validationErrors[field as keyof ValidationErrors]) {
      delete this.validationErrors[field as keyof ValidationErrors];
    }
  }

  // Validation
  private validateForm() {
    const errors: ValidationErrors = {};

    if (!this.formData.jobNumber) {
      errors.jobNumber = "Job number is required";
    }

    if (!this.formData.name?.trim()) {
      errors.name = "Project name is required";
    }

    if (this.formData.budget && this.formData.budget < 0) {
      errors.budget = "Budget cannot be negative";
    }

    runInAction(() => {
      this.validationErrors = errors;
    });
  }

  // Form actions
  async saveProject(): Promise<boolean> {
    if (!this.isValid) {
      return false;
    }

    this.saving = true;
    this.error = null;

    try {
      if (this.isEditing && this.jobId) {
        await this.jobStore.patch(
          this.jobId,
          this.formData as unknown as Parameters<typeof this.jobStore.patch>[1]
        );
      } else {
        await this.jobStore.create(
          this.formData as unknown as Parameters<typeof this.jobStore.create>[0]
        );
      }
      return true;
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to save job";
      console.error("Failed to save job:", error);
      return false;
    } finally {
      this.saving = false;
    }
  }

  resetForm() {
    this.formData = {
      jobNumber: null,
      name: "",
      shortName: "",
      poNumber: "",
      site: "",
      address: "",
      city: "",
      stateLocation: "",
      zip: "",
      engineer: "",
      projectManager: "",
      fieldForeman: "",
      description: "",
      stage: "",
      floorCount: null,
      accountId: null,
      budget: null,
      actualCost: null,
      priority: "Medium",
      progress: 0,
    };
    this.originalData = null;
    this.isEditing = false;
    this.jobId = null;
    this.validationErrors = {};
    this.isDirty = false;
    this.error = null;
  }

  // Utility methods
  get stageOptions() {
    return ["Planning", "In Progress", "On Hold", "Completed", "Cancelled"];
  }

  get priorityOptions() {
    return ["Low", "Medium", "High", "Critical"];
  }
  
  get statusOptions() {
    return this.stageOptions;
  }
  
  get typeOptions() {
    return this.stageOptions;
  }

  // Cleanup
  dispose() {
    // Clean up any subscriptions or timers if needed
  }
}

export default ProjectFormStore;
